import i18n from 'infrastructure/i18n';
import useUser from 'utils/useUser';
import { useCheckRoleAccess } from 'infrastructure/hooks';
import { isCombinedModule } from 'infrastructure/functions';
import { ActivityStatus } from 'infrastructure/enums';

import s from './styles.module.scss';
import DateColumn from './components/date-col';
import { useCellHeights } from './hooks/use-cell-heights';
import MobileCell from './mobile-cell';
import ReadingCell from './reading-cell';
import CommentCell from './comment-cell';

import type { TView } from './types';
import type {
  TRpmDashboardActivityQueryKey,
  TRpmPatientActivityQueryKey,
} from 'infrastructure/hooks/rpm';
import type { IBaseTableColumns } from 'components/atoms/base-table/types';
import type {
  IActivityComments,
  IActivityData,
} from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { ModuleType, ReadingType } from 'infrastructure/enums';

interface Props {
  view: TView;
  getActivityQueryKey:
    | TRpmDashboardActivityQueryKey
    | TRpmPatientActivityQueryKey;
  tableTypePrefix?: 'a' | 'u';
  module: ModuleType;
  type?: ActivityStatus;
  status?: ActivityStatus;
  loading: boolean;
}

export const useActivityColumns = ({
  view,
  getActivityQueryKey,
  tableTypePrefix,
  module,
  type,
  status,
  loading,
}: Props) => {
  const { rowHeights, rowRefs } = useCellHeights(getActivityQueryKey);
  const {
    isCriticalOrAtRiskAlertingRole,
    isMobileDevice,
    isFullyManagedAgency,
    isAtRiskRole,
    isAgencyDoctor,
  } = useUser();
  const { isReadOnlyRole } = useCheckRoleAccess();

  // This is temporary solution, delete it after new dashboard release
  const hideForFullyManagedAgency =
    isFullyManagedAgency && isAgencyDoctor && !isAtRiskRole;

  const hideCommentsColumn =
    view === 'dashboard' &&
    (hideForFullyManagedAgency || isCriticalOrAtRiskAlertingRole);
  const isShowAssignedToggle = status !== ActivityStatus.Erroneous;
  const isShowReviewColumn = isCombinedModule(module)
    ? !isReadOnlyRole
    : status !== ActivityStatus.Erroneous;

  const activityColumnsMobile: IBaseTableColumns<IActivityData> = [
    {
      label: i18n.t('labels.time'),
      key: 'createdAt',
      width: '95px',
      sortable: true,
      sortKey: 'createdAt',
      onCell: () => ({
        colSpan: 3,
      }),
      render: (createdAt: string, record, idx) => (
        <MobileCell
          createdAt={createdAt}
          record={record}
          idx={tableTypePrefix ? `${tableTypePrefix}-${idx}` : idx}
          showReviewCol={isShowReviewColumn && !record.isErroneous}
          view={view}
          getActivityQueryKey={getActivityQueryKey}
          hideCommentsColumn={hideCommentsColumn}
          module={module}
        />
      ),
    },
    {
      label: i18n.t('labels.reading'),
      key: 'readingType',
      onCell: () => ({
        colSpan: 0,
      }),
    },
    {
      label: (
        <p className={s['comments-title']}>
          {isShowReviewColumn && <span>{i18n.t('labels.reviewed')}</span>}
        </p>
      ),
      key: 'comments',
      width: '80px',
      onCell: () => ({
        colSpan: 0,
      }),
    },
  ];

  const activityColumns: IBaseTableColumns<IActivityData> = [
    {
      label: i18n.t('labels.time'),
      key: 'createdAt',
      width: '140px',
      sortable: true,
      sortKey: 'createdAt',
      render: (createdAt: string, record, idx) => (
        <DateColumn
          createdAt={createdAt}
          timezone={record.patient.timezone}
          globalStatus={record.globalStatus}
          idx={idx}
          view={view}
          height={rowHeights.get(idx)}
          getActivityQueryKey={getActivityQueryKey}
          hideCommentsColumn={hideCommentsColumn}
          thresholdName={record.thresholdName}
          thresholds={record.patient.threshold}
          mightBeErroneousReason={record.mightBeErroneousReason}
          isRecovered={record.isRecovered}
          recoveredByReadingId={record.recoveredByReadingId}
          patientGuid={record.patient.guid}
          receivedTime={record.receivedTime}
          readingTime={record.dateTime}
        />
      ),
    },
    {
      label: i18n.t('labels.reading'),
      key: 'readingType',
      sortable: view === 'dashboard',
      sortKey: 'name',
      width: hideCommentsColumn ? undefined : '235px',
      render: (readingType: ReadingType, record, idx) => (
        <ReadingCell
          record={record}
          idx={idx}
          view={view}
          getActivityQueryKey={getActivityQueryKey}
          hideCommentsColumn={hideCommentsColumn}
          readingType={readingType}
          rowRefs={rowRefs}
          isShowAssignedToggle={isShowAssignedToggle}
        />
      ),
    },
    {
      label: (
        <p className={s['comments-title']}>
          <span>{i18n.t('labels.comments')}</span>
          {isShowReviewColumn && <span>{i18n.t('labels.reviewed')}</span>}
        </p>
      ),
      key: 'comments',
      render: (comments: IActivityComments[], record) => (
        <CommentCell
          createdAt={record.createdAt}
          idx={
            tableTypePrefix
              ? `${tableTypePrefix}-${record.deviceId}-${record.id}`
              : `${record.deviceId}-${record.id}`
          }
          record={record}
          status={status}
          getActivityQueryKey={getActivityQueryKey}
          view={view}
          module={module}
          type={type}
          loading={loading}
        />
      ),
    },
  ];

  return isMobileDevice
    ? activityColumnsMobile
    : activityColumns.filter((col) =>
        hideCommentsColumn ? col.key !== 'comments' : true,
      );
};
